import * as React from "react";
import type { Column, Registrant } from "../api.js";
import { getCellValue } from "../filter.js";
import { formatBool, formatCurrency, formatDate, formatDateTime } from "../format.js";
import { Icon } from "./Icon.js";

const AVATAR_PALETTE = [
  "#1a8fbf", "#14a89a", "#ff7a59", "#7c5cff", "#e94e77",
  "#f5a623", "#2ea66a", "#0f5f84", "#a8345a", "#0e7a70",
];

function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]!;
}

function initials(row: Registrant): string {
  const first = row.firstName?.trim();
  const last = row.lastName?.trim();
  if (first && last) return (first[0]! + last[0]!).toUpperCase();
  const name = (row.fullName || "").trim();
  if (!name) return "?";
  const parts = name.split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function Cell({ row, column }: { row: Registrant; column: Column }) {
  if (column.id === "fullName") {
    return (
      <div className="cell-name">
        <div className="avatar" style={{ background: avatarColor(row.contactId || row.fullName) }}>
          {initials(row)}
        </div>
        <span className="nm">{row.fullName}</span>
      </div>
    );
  }
  const v = getCellValue(row, column);
  if (v === null || v === undefined || v === "") {
    return <span style={{ color: "var(--ink-400)" }}>—</span>;
  }
  switch (column.type) {
    case "bool": {
      const b = Boolean(v);
      return b
        ? <span className="cell-bool-yes"><Icon name="check" size={16} /></span>
        : <span className="cell-bool-no"><Icon name="x" size={14} /></span>;
    }
    case "currency": {
      const n = typeof v === "number" ? v : Number(v);
      return <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(n)}</span>;
    }
    case "date":
      return <span style={{ color: "var(--ink-600)", fontVariantNumeric: "tabular-nums" }}>{formatDate(String(v))}</span>;
    case "datetime":
      return <span style={{ color: "var(--ink-600)", fontVariantNumeric: "tabular-nums" }}>{formatDateTime(String(v))}</span>;
    case "email":
      return <a href={`mailto:${String(v)}`} onClick={(e) => e.stopPropagation()}>{String(v)}</a>;
    case "phone":
      return <a href={`tel:${String(v)}`} onClick={(e) => e.stopPropagation()}>{String(v)}</a>;
    case "tags":
      return (
        <div className="cell-tags">
          {(Array.isArray(v) ? v : String(v).split(/[,;]/)).map((t, i) => {
            const text = String(t).trim();
            if (!text) return null;
            return <span key={i} className={`chip ${text.toLowerCase() === "none" ? "gray" : "teal"}`}>{text}</span>;
          })}
        </div>
      );
    default: {
      if (column.id === "gender") {
        const g = String(v);
        const cls = g[0] === "F" ? "cell-gender-F" : g[0] === "M" ? "cell-gender-M" : "";
        return <span className={cls} style={{ fontWeight: 600 }}>{g}</span>;
      }
      if (column.id === "eventName") {
        return <span style={{ color: "var(--ink-700)" }}>{String(v)}</span>;
      }
      if (column.type === "bool") return <span>{formatBool(Boolean(v))}</span>;
      if (Array.isArray(v)) return <span>{v.join(", ")}</span>;
      if (typeof v === "object") {
        const obj = v as Record<string, unknown>;
        const label = obj.title ?? obj.label ?? obj.name;
        return <span>{typeof label === "string" ? label : JSON.stringify(v)}</span>;
      }
      return <span>{String(v)}</span>;
    }
  }
}
